import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import "../../styles/components/Inbox.css";
import { getMessagesForUser } from "../services/soar";
import DirectMessages from "./DirectMessages";
import ServiceMessages from "./ServiceMessages";
import _ from "lodash";
import { DateTime } from "luxon";

const Inbox = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const [loadStatus, setLoadStatus] = useState({});
  const [activeTab, setActiveTab] = useState("direct");
  const [controllerSignal, setControllerSignal] = useState();

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (
      controllerSignal &&
      userId &&
      !loadStatus.messagesLoaded &&
      !loadStatus.loadingMessages
    ) {
      const loadMessages = async () => {
        setLoadStatus((ls) => ({ ...ls, loadingMessages: true }));
        const response = await getMessagesForUser(userId, controllerSignal);
        if (!controllerSignal.aborted) {
          if (response) {
            setMessages(
              _.sortBy(response, (m) => DateTime.now(m.CreatedOn)).reverse()
            );
          }

          setLoadStatus((ls) => ({
            ...ls,
            loadingMessages: false,
            messagesLoaded: true,
          }));
        }
      };

      loadMessages();
    }
  }, [
    controllerSignal,
    userId,
    loadStatus.loadingMessages,
    loadStatus.messagesLoaded,
  ]);

  const directMessages = useMemo(() => {
    return messages.filter((m) => !m.RecordedServiceId);
  }, [messages]);

  const serviceMessages = useMemo(() => {
    return messages.filter((m) => m.RecordedServiceId);
  }, [messages]);

  const unreadDirectClients = useMemo(() => {
    const clients = [];
    const groupedClientsObj = _.groupBy(directMessages, (m) => m.ClientId);

    for (const client in groupedClientsObj) {
      if (Object.prototype.hasOwnProperty.call(groupedClientsObj, client)) {
        const fosterParentMessages = _.groupBy(
          groupedClientsObj[client],
          (m) => m.FosterParentId
        );

        for (const fosterParent in fosterParentMessages) {
          if (
            Object.prototype.hasOwnProperty.call(
              fosterParentMessages,
              fosterParent
            )
          ) {
            const latestMessage = fosterParentMessages[fosterParent][0];
            if (
              latestMessage.DiscussionItemStatus === "Sent" &&
              latestMessage.CreatedById === latestMessage.FosterParentId
            ) {
              clients.push(client);
            }
          }
        }
      }
    }

    return clients;
  }, [directMessages, userId]);

  const unreadServiceClients = useMemo(() => {
    const clients = [];
    const groupedClientsObj = _.groupBy(serviceMessages, (m) => m.ClientId);

    for (const client in groupedClientsObj) {
      if (Object.prototype.hasOwnProperty.call(groupedClientsObj, client)) {
        const sMessages = _.groupBy(
          groupedClientsObj[client],
          (m) => m.RecordedServiceId
        );

        for (const teamMember in sMessages) {
          if (Object.prototype.hasOwnProperty.call(sMessages, teamMember)) {
            const latestMessage = sMessages[teamMember][0];
            if (
              latestMessage.DiscussionItemStatus === "Sent" &&
              latestMessage.CreatedById === latestMessage.FosterParentId
            ) {
              console.log(latestMessage);
              clients.push(client);
            }
          }
        }
      }
    }

    return clients;
  }, [serviceMessages, userId]);

  useEffect(() => {
    localStorage.setItem(
      "unreadMessagesCount",
      unreadDirectClients.length + unreadServiceClients.length
    );
  }, [unreadDirectClients, unreadServiceClients]);

  const [viewportWidth, setViewportWidth] = useState(
    Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  );

  const [viewportHeight, setViewportHeight] = useState(
    Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    )
  );

  useEffect(() => {
    const getViewportDimensions = () => {
      setViewportWidth(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      );
      setViewportHeight(
        Math.max(
          document.documentElement.clientHeight || 0,
          window.innerHeight || 0
        )
      );
    };

    window.addEventListener("resize", getViewportDimensions, true);
    return () => window.removeEventListener("resize", getViewportDimensions);
  }, []);

  return (
    <div
      className={`safy-inbox${
        viewportWidth < 576 || viewportHeight < 900 ? " small-inbox" : ""
      }`}
    >
      <h5 className="ms-4">Message Center</h5>
      <Tabs
        activeKey={activeTab}
        id="messages-tabs"
        className="cptabs"
        transition={false}
        onSelect={setActiveTab}
      >
        <Tab eventKey="direct" title="Direct Messages" className="cptabs">
          <DirectMessages
            directMessages={directMessages}
            unreadClients={unreadDirectClients}
          />
        </Tab>
        <Tab eventKey="service" title="Service Messages" className="cptabs">
          <ServiceMessages
            serviceMessages={serviceMessages}
            unreadClients={unreadServiceClients}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

export default Inbox;
